import { getTotalClassLevel } from './classIdentity.mjs';

export const SPECIES_USES = Object.freeze({
  'goliath-large-form': { species: 'goliath', level: 5, maximum: 1, rest: 'long' },
  'dragonborn-draconic-flight': { species: 'dragonborn', level: 5, maximum: 1, rest: 'long' },
  'orc-adrenaline-rush': { species: 'orc', level: 1, maximum: 'proficiency', rest: 'short' },
  // Breath Weapon: Proficiency Bonus uses, all regained on a Long Rest.
  'dragonborn-breath-weapon': { species: 'dragonborn', level: 1, maximum: 'proficiency', rest: 'long' },
  // Relentless Endurance: once per Long Rest, spent by the server when damage would drop the Orc to 0 Hit Points.
  'orc-relentless-endurance': { species: 'orc', level: 1, maximum: 1, rest: 'long', automatic: true },
  // Giant Ancestry (Goliath): the chosen option's Proficiency Bonus uses per Long Rest.
  'goliath-giant-ancestry': { species: 'goliath', level: 1, maximum: 'proficiency', rest: 'long' },
  // Stonecunning: Proficiency Bonus uses per Long Rest.
  'dwarf-stonecunning': { species: 'dwarf', level: 1, maximum: 'proficiency', rest: 'long' },
});
// Fiendish Legacy's free casts, kept with the other free casts in spellcastingState.speciesFreeCasts. Hellish Rebuke is
// left out: it casts from its own Reaction prompt.
const LEGACY_FREE_SPELLS = Object.freeze({
  abyssal: [{ spellId: 'ray-of-sickness', level: 1, gained: 3 }, { spellId: 'hold-person', level: 2, gained: 5 }],
  chthonic: [{ spellId: 'false-life', level: 1, gained: 3 }, { spellId: 'ray-of-enfeeblement', level: 2, gained: 5 }],
  infernal: [{ spellId: 'darkness', level: 2, gained: 5 }],
});
const tieflingLegacyKey = (character) => {
  const race = character?.race;
  if (String(typeof race === 'string' ? race : race?.name || race?.Name || '').trim().toLowerCase() !== 'tiefling') return '';
  return String(character?.tieflingLegacyKey || race?.selectedAncestryKey || '').trim().toLowerCase();
};
/** An Infernal Tiefling gains Hellish Rebuke at level 3. */
export const hasInfernalRebuke = (character) => tieflingLegacyKey(character) === 'infernal' && getTotalClassLevel(character) >= 3;
/** The Tiefling's free legacy spells at this level, with `remaining` of the one cast per Long Rest. */
export const fiendishLegacyFreeSpells = (character) => {
  const legacy = tieflingLegacyKey(character);
  const level = getTotalClassLevel(character);
  return (LEGACY_FREE_SPELLS[legacy] || []).filter((spell) => level >= spell.gained).map(({ spellId, level: spellLevel }) => {
    const grantId = `tiefling-${legacy}-${spellId}`;
    return { grantId, spellId, level: spellLevel, remaining: Number(character?.spellcastingState?.speciesFreeCasts?.[grantId]) > 0 ? 0 : 1 };
  });
};
export const speciesRevision = character => Math.max(0, Math.floor(Number(character?.speciesResources?.revision) || 0));
export function speciesUseProfile(character, id, proficiency) {
  const rule = SPECIES_USES[id];
  const race = character?.race;
  const species = String(typeof race === 'string' ? race : race?.name || race?.Name || '').trim().toLowerCase();
  const eligible = Boolean(rule && species === rule.species && getTotalClassLevel(character) >= rule.level);
  const maximum = eligible ? rule.maximum === 'proficiency' ? Math.max(0, Math.floor(Number(proficiency) || 0)) : rule.maximum : 0;
  const spent = Math.max(0, Math.floor(Number(character?.speciesResources?.spent?.[id]) || 0));
  return { eligible, maximum, spent, remaining: Math.max(0, maximum - spent) };
}
export function speciesRestState(character, type) {
  const previous = character?.speciesResources || {};
  const spent = { ...previous.spent };
  for (const [id, rule] of Object.entries(SPECIES_USES)) if (type === 'long' || rule.rest === type) delete spent[id];
  return { ...previous, spent, revision: speciesRevision(character) + 1 };
}
export function mergeSpeciesResources(character, incoming) {
  if (!character || !incoming || !Number.isInteger(incoming.revision) || incoming.revision < speciesRevision(character)) return character;
  return { ...character, speciesResources: incoming };
}
